import {Button, Card, openDialog, TabProps, TabsHeader, Tooltip} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';

import {useRef, useState} from 'react';

import {isNotNilOrEmpty, isString} from 'ramda-adjunct';

import {TenantResponseBody} from '@dms/api/shared';
import {GetWarehouseResponse, PostReceiveNoteBasketItemRequest} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {usePermissions} from '@dms/shared';
import {ArticleCreateDefaultValues, getSingleOrUndefined} from '@dms/shared-aftersales';

import {composePath, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {ExternalFilters} from 'features/eag-grid';

import {RECEIVE_NOTE_ITEM_DIALOG} from '../constants/receiveNoteItemDialog';
import {ItemRowData} from '../types/ItemRowData';
import {ReceiveNoteItemDetail} from './ReceiveNoteItemDetail';
import {ReceiveNoteMaterialPriceListTab} from './ReceiveNoteMaterialPriceListTab';
import {ReceiveNoteMaterialWarehouseTab} from './ReceiveNoteMaterialWarehouseTab';

interface ReceiveNoteMaterialListProps extends RequiredTestIdProps {
  receiveNoteId: string;
  authorizationProfileId: string | Nullish;
  branchId: string | Nullish;
  warehouse: GetWarehouseResponse | Nullish;
  tenant: TenantResponseBody | Nullish;
  state: 'PENDING' | 'COMPLETED' | Nullish;
  onAddToBasket: (requestBody: PostReceiveNoteBasketItemRequest['body']) => Promise<void>;
  isAddingToBasket: boolean;
  hasPendingRequest: boolean;
}

const TABS_IDS = {
  warehouseTab: 'receiveNote-addMaterial-warehouseTab',
  priceListTab: 'receiveNote-addMaterial-priceListTab',
};

export function ReceiveNoteMaterialList(props: ReceiveNoteMaterialListProps) {
  const [activeTabId, setActiveTabId] = useState(TABS_IDS.warehouseTab);
  const [activeFolderId, setActiveFolderId] = useState<string>();
  const filters = useRef<ArticleCreateDefaultValues>({});

  const [articleCreate] = usePermissions({
    permissionKeys: ['articleCreate'],
  });

  const handleCreateArticle = () => {
    openDialog(
      <ReceiveNoteItemDetail
        mode="creating"
        context="BASKET"
        state={props.state}
        authorizationProfileId={props.authorizationProfileId}
        receiveNoteId={props.receiveNoteId}
        warehouse={props.warehouse}
        tenant={props.tenant}
        isAddingToBasket={props.isAddingToBasket}
        onAddToBasket={props.onAddToBasket}
        data-testid={suffixTestId('forms.editItem', props)}
        defaultValues={{
          activeFolderId,
          catalogNumber: filters.current?.catalogNumber,
          manufacturerId: filters.current?.manufacturerId,
          warehouseId: props.warehouse?.id,
          name: filters.current?.name,
        }}
      />,
      {
        id: RECEIVE_NOTE_ITEM_DIALOG,
        title: i18n.t('general.labels.edit'),
        size: 'large',
        withAdditionalFooter: true,
        'data-testid': suffixTestId('dialogs.editItem', props),
      }
    );
  };

  const handleOpenReceiveNoteItemDetail = (itemRowData: ItemRowData) => {
    openDialog(
      <ReceiveNoteItemDetail
        mode="adding"
        context="BASKET"
        authorizationProfileId={props.authorizationProfileId}
        receiveNoteId={props.receiveNoteId}
        receiveNoteItemId={itemRowData.itemId}
        itemRowData={itemRowData}
        warehouse={props.warehouse}
        tenant={props.tenant}
        state={props.state}
        onAddToBasket={props.onAddToBasket}
        isAddingToBasket={props.isAddingToBasket}
        data-testid={suffixTestId('dialogs.addItem', props)}
      />,
      {
        id: RECEIVE_NOTE_ITEM_DIALOG,
        title: i18n.t('entity.warehouse.labels.addReceiveItem'),
        size: 'large',
        withAdditionalFooter: true,
        'data-testid': suffixTestId('dialogs.addReceiveItem', props),
      }
    );
  };

  const handleArticleDetail = (warehouseId: string, articleId: string) => {
    const path = composePath(warehouseRoutes.articleDetailOverview, {
      params: {warehouseId, id: articleId},
      isSearchParamsPreserved: false,
    });
    window.open(path, '_blank');
  };

  const handleFiltersChange = (newFilters: ExternalFilters) => {
    const articleCreateDefaultValues: ArticleCreateDefaultValues = {};

    if (isString(newFilters.catalogNumber)) {
      articleCreateDefaultValues.catalogNumber = newFilters.catalogNumber;
    }

    if (isNotNilOrEmpty(newFilters.manufacturerId)) {
      articleCreateDefaultValues.manufacturerId = getSingleOrUndefined(newFilters.manufacturerId);
    }

    if (isString(newFilters.name)) {
      articleCreateDefaultValues.name = newFilters.name;
    }

    filters.current = articleCreateDefaultValues;
  };

  const tabs: TabProps[] = [
    {
      id: TABS_IDS.warehouseTab,
      title: i18n.t('entity.warehouse.labels.warehouse'),
      content: (
        <ReceiveNoteMaterialWarehouseTab
          onAddToBasket={handleOpenReceiveNoteItemDetail}
          onArticleDetail={handleArticleDetail}
          onActiveFolderIdChange={setActiveFolderId}
          onExternalFilterChange={handleFiltersChange}
          hasPendingRequest={props.hasPendingRequest}
          data-testid={suffixTestId('tabs.warehouse', props)}
        />
      ),
    },
    {
      id: TABS_IDS.priceListTab,
      title: i18n.t('entity.warehouse.labels.priceList'),
      content: (
        <ReceiveNoteMaterialPriceListTab
          warehouseId={props.warehouse?.id ?? ''}
          authorizationProfileId={props.authorizationProfileId}
          branchId={props.branchId}
          onAddToBasket={handleOpenReceiveNoteItemDetail}
          hasPendingRequest={props.hasPendingRequest}
          data-testid={suffixTestId('tabs.priceList', props)}
        />
      ),
    },
  ];

  const activeTabContent = tabs.find(
    (tab) => tab.id === (activeTabId ?? TABS_IDS.warehouseTab)
  )?.content;

  return (
    <VStack spacing={4} height="100%">
      <HStack justify="space-between">
        <TabsHeader
          variant="condensed"
          tabs={tabs}
          activeTabId={activeTabId}
          onChange={setActiveTabId}
          data-testid={suffixTestId('tabs', props)}
        />
        <Show when={activeTabId === TABS_IDS.warehouseTab}>
          <Tooltip
            isDisabled={articleCreate}
            label={i18n.t('module.warehouse.label.missingArticleCreatePermission')}
          >
            <Button
              variant="secondary"
              leftIcon="content/add_circle"
              onClick={handleCreateArticle}
              title={i18n.t('entity.warehouse.labels.createNewArticle')}
              isDisabled={!articleCreate}
              data-testid={suffixTestId('button.createNewArticle', props)}
            />
          </Tooltip>
        </Show>
      </HStack>
      <Card isFullHeight>
        <Box height="100%" flex={1}>
          {activeTabContent}
        </Box>
      </Card>
    </VStack>
  );
}
